import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Box } from '@mui/material';

import PlanContainer from '../../../../components/PlanContainer';
import CtaButton from '../../../../components/CtaButton';
import PlanPrice from '../../../../components/PlanPrice';
import PlanDetailsContainer from '../../../../components/PlanDetailsContainer';
import CheckMarkList from '../../../../components/CheckMarkList';
import PlanDescription from '../../../../components/PlanDescription';
import PlanExampleContainer from '../../../../components/PlanExampleContainer';
import usePriceConfig from '../../../../../shared/features/priceConfig/hooks/usePriceConfig';
import {
  getCurrencyForAreaUnit,
  getCurrencySignForAreaUnit,
} from '../../../../../shared/helpers/functions/currency';
import FeaturesList from '../FeaturesList';
import { getMonthlyPlanCheckoutLink } from '../../../../../shared/helpers/functions/navigation';
import { Plan } from '../../../../../shared/helpers/constants/plans';
import { getPluralAreaUnit } from '../../../../../shared/features/areaUnit/helpers/functions/areaUnit';
import rootStore from '../../../rootStore/RootStore';
import InitialCostsCalculatorTable from '../InitialCostsCalculatorTable';
import MonthlyCostsCalculatorTable from '../MonthlyCostsCalculatorTable';
import AmplitudeAnalytics from '../../../../../shared/services/AmplitudeAnalytics';

const MonthlyPlan = observer(() => {
  const { t } = useTranslation();
  const { config, isLoaded: priceConfigLoaded } = usePriceConfig(rootStore);
  const { areaUnitStore } = rootStore;
  const { areaUnit } = areaUnitStore;

  const currency = getCurrencyForAreaUnit(areaUnit);
  const currencySign = getCurrencySignForAreaUnit(areaUnit);
  const pluralAreaUnit = getPluralAreaUnit(areaUnit);

  const monthlyFee =
    config?.MonthlyPayAsYouGo.BaseMonthlyFee?.prices[currency]?.price;

  const monthlyPlanCheckoutLink = getMonthlyPlanCheckoutLink(areaUnit);

  const handleCtaClick = () => {
    AmplitudeAnalytics.trackPlanSelect(Plan.monthly);
  };

  return (
    <PlanContainer showPlaceholder={!priceConfigLoaded}>
      <PlanDescription>
        <Trans
          key="title"
          i18nKey="plans.monthly.title"
          components={{
            1: (
              <Box
                sx={{
                  letterSpacing: '-2px',
                }}
                component="span"
              >
                Pay-As-You-Go
              </Box>
            ),
          }}
        />
        <Fragment key="description">{t('plans.monthly.description')}</Fragment>
        <PlanPrice
          key="price"
          price={{
            period: t('plan-select.price-details.period.month'),
            value: `${currencySign}${monthlyFee}`,
          }}
        />
      </PlanDescription>
      <CtaButton
        label={t('plan-select.action-button.start-now')}
        to={monthlyPlanCheckoutLink}
        onClick={handleCtaClick}
      />
      <PlanDetailsContainer>
        <FeaturesList />
      </PlanDetailsContainer>
      <PlanExampleContainer>
        <Fragment key="title">
          {t('plan-select.pricing-example.title')}
        </Fragment>
        <Fragment key="description">
          {t('plans.monthly.pricing-example.initial-month-description', {
            areaUnit: pluralAreaUnit,
          })}
        </Fragment>
        <Fragment key="content">
          <InitialCostsCalculatorTable />
          <MonthlyCostsCalculatorTable />
        </Fragment>
      </PlanExampleContainer>
      <CheckMarkList
        title={t('plan-select.benefits')}
        items={[
          {
            title: t('plans.monthly.benefits.flexibility.title'),
            description: t('plans.monthly.benefits.flexibility.description'),
          },
          {
            title: t('plans.monthly.benefits.cost-control.title'),
            description: t('plans.monthly.benefits.cost-control.description'),
          },
          {
            title: t('plans.monthly.benefits.transparency.title'),
            description: t('plans.monthly.benefits.transparency.description'),
          },
          {
            title: t('plans.monthly.benefits.scalability.title'),
            description: t('plans.monthly.benefits.scalability.description'),
          },
        ]}
      />
    </PlanContainer>
  );
});

export default MonthlyPlan;
